
import { IFlightSelectOption } from "@/feature/flight/search/state/slices/extract-flight-filter.slice";
import { IFlight } from "@/feature/flight/search/types/flight";

// Constants moved outside the function
const TIME_RANGES = {
  '00-06': { start: 0, end: 6 },
  '06-12': { start: 6, end: 12 },
  '12-18': { start: 12, end: 18 },
  '18-00': { start: 18, end: 0 },
};

const parseHour = (timeString: string): number => parseInt(timeString.split(':')[0], 10);
const isTimeInRange = (hour: number, range: { start: number; end: number }): boolean =>
  range.start < range.end ? hour >= range.start && hour < range.end : hour >= range.start || hour < range.end;
const getTimeFrameKey = (index: number, type: string) => `${index}_${type}`;

export const filterFlightResult = (
  flightList: IFlight[] | undefined,
  filter: IFlightSelectOption
) => {
  if (!flightList) return;


  const {
    airline = [],
    layover_city = [],
    aircraft = [],
    price_range = [],
    stoppage = {},
    flight_number = [],
    timeFrame = [],
    refundable,
    min_layover,
    max_layover,
    type
  } = filter;

  // Convert arrays to Sets once
  const airlineSet = new Set(airline);
  const layoverCitySet = new Set(layover_city);
  const aircraftSet = new Set(aircraft);
  const flightNumberSet = new Set(flight_number.map(String));

  const [minPrice = -Infinity, maxPrice = Infinity] = price_range;

  // Precompute time frames
  const timeFrames = timeFrame.reduce((acc, frame) => {
    const key = getTimeFrameKey(frame.arr_index, frame.type);
    acc[key] = TIME_RANGES[frame.time as keyof typeof TIME_RANGES];
    return acc;
  }, {} as Record<string, { start: number; end: number }>);

  // Precompute layover constraints
  const minLayover = min_layover?.value ?? -Infinity;
  const maxLayover = max_layover?.value ?? Infinity;
  const layoverIndex = min_layover?.index ?? max_layover?.index;

  return flightList.filter(flight => {
    // 1. Price check (fastest possible exit)
    const payable = flight.fare.payable;
    if (payable < minPrice || payable > maxPrice) return false;

    // 2. Airline check
    if (airlineSet.size > 0) {
      const airlineName = flight.carrier_name;
      if (!airlineName || !airlineSet.has(airlineName)) return false;
    }

    // 3. Refundable check
    if (refundable !== undefined && flight.refundable !== refundable) return false;

    // 4. Stoppage check (per-leg optimized)
    const stoppageKeys = Object.keys(stoppage);
    if (stoppageKeys.length > 0) {
      for (const legIndexStr of stoppageKeys) {
        const legIndex = parseInt(legIndexStr, 10);
        const allowedStops = stoppage[legIndex];

        if (!allowedStops?.length) continue;
        if (legIndex >= flight.flights.length) return false;

        const legStops = flight.flights[legIndex].stoppage;
        if (!allowedStops.includes(legStops)) return false;
      }
    }

    // 5. Flight number check (with early exit)
    if (flightNumberSet.size > 0) {
      let foundFlightNumber = false;

      for (const leg of flight.flights) {
        for (const option of leg.options || []) {
          const opFlight = option.carrier?.carrier_operating_flight_number;
          const mkFlight = option.carrier?.carrier_marketing_flight_number;

          if ((opFlight && flightNumberSet.has(String(opFlight))) ||
            (mkFlight && flightNumberSet.has(String(mkFlight)))) {
            foundFlightNumber = true;
            break;
          }
        }
        if (foundFlightNumber) break;
      }

      if (!foundFlightNumber) return false;
    }

    // 6. Time frame check (optimized with precomputed keys)
    const timeFrameKeys = Object.keys(timeFrames);
    if (timeFrameKeys.length > 0) {
      for (let i = 0; i < flight.flights.length; i++) {
        const flightLeg = flight.flights[i];
        const departureKey = getTimeFrameKey(i, 'departure');
        const arrivalKey = getTimeFrameKey(i, 'arrival');

        let legValid = false;

        for (const option of flightLeg.options || []) {
          let optionValid = true;

          if (timeFrames[departureKey]) {
            const hour = parseHour(option.departure.time);
            optionValid &&= isTimeInRange(hour, timeFrames[departureKey]);
          }

          if (timeFrames[arrivalKey] && optionValid) {
            const hour = parseHour(option.arrival.time);
            optionValid &&= isTimeInRange(hour, timeFrames[arrivalKey]);
          }

          if (optionValid) {
            legValid = true;
            break;
          }
        }

        if (!legValid) return false;
      }
    }

    // 7. Layover city check
    if (layoverCitySet.size > 0) {
      let foundCity = false;

      for (const leg of flight.flights) {
        for (const option of leg.options || []) {
          if (layoverCitySet.has(option.arrival?.airport_code) ||
            layoverCitySet.has(option.departure?.airport_code)) {
            foundCity = true;
            break;
          }
        }
        if (foundCity) break;
      }

      if (!foundCity) return false;
    }

    // 8. Aircraft check
    if (aircraftSet.size > 0) {
      let foundAircraft = false;

      for (const leg of flight.flights) {
        for (const option of leg.options || []) {
          const acName = option.carrier?.carrier_aircraft_name;
          if (acName && aircraftSet.has(acName)) {
            foundAircraft = true;
            break;
          }
        }
        if (foundAircraft) break;
      }

      if (!foundAircraft) return false;
    }

    // 9. Layover time check
    if (layoverIndex !== undefined) {
      const layoverTimes = flight.flights[layoverIndex]?.layover_time || [];
      for (const time of layoverTimes) {
        if (time < minLayover || time > maxLayover) {
          return false;
        }
      }
    }

    return true;
  }).sort((a, b) => {
    // Optimized sorting
    if (!type) return 0;

    switch (type) {
      case 'CHEAPEST':
        return a.fare.payable - b.fare.payable;
      case 'SHORTEST': {
        const timeA = a.flights[0]?.elapsed_time || 0;
        const timeB = b.flights[0]?.elapsed_time || 0;
        return timeA - timeB;
      }

      case 'EARLIEST': {
        const timeA = a.flights[0]?.options[0]?.departure.time || '23:59';
        const timeB = b.flights[0]?.options[0]?.departure.time || '23:59';
        return timeA.localeCompare(timeB);
      }

      default:
        return 0;
    }
  });
};

